import React from 'react';
import { motion } from 'framer-motion';
import { Container, Row, Col } from 'reactstrap';

interface Integration {
  name: string;
  logo: string;
  description: string;
  status: 'active' | 'coming-soon';
  category: 'marketplace' | 'supplier';
  features?: string[];
}

const integrations: Integration[] = [
  // Marketplaces
  {
    name: 'eBay',
    logo: '/images/logos/EBay_logo.svg.png',
    description: 'Full API integration with 8 eBay marketplaces — UK, US, DE, FR, IT, ES, AU, CA. List products, manage orders, and sync inventory automatically.',
    status: 'active',
    category: 'marketplace',
    features: ['Auto-listing', 'Order sync', 'Repricing', 'Templates']
  },
  {
    name: 'Shopify',
    logo: '/images/logos/Shopify_logo_2018.svg.png',
    description: 'Connect your Shopify store for multi-channel selling. Sync products and orders seamlessly.',
    status: 'coming-soon',
    category: 'marketplace',
    features: ['Product sync', 'Order management', 'Inventory sync']
  },
  {
    name: 'Amazon',
    logo: '/images/logos/Amazon_logo.svg.png',
    description: 'Sell on Amazon marketplace. Expand your reach to millions of Prime customers.',
    status: 'coming-soon',
    category: 'marketplace',
    features: ['FBA support', 'Prime badge', 'Buy Box optimization']
  },
  {
    name: 'Walmart',
    logo: '/images/logos/walmart-logo.png',
    description: 'Access the US market through Walmart Marketplace. Tap into 120M+ monthly visitors.',
    status: 'coming-soon',
    category: 'marketplace',
    features: ['US market access', 'Competitive fees', 'Growing platform']
  },
  {
    name: 'TikTok Shop',
    logo: '/images/logos/TikTok_logo.svg.png',
    description: 'Sell through social commerce. Reach Gen Z and millennial shoppers where they spend time.',
    status: 'coming-soon',
    category: 'marketplace',
    features: ['Social selling', 'Viral potential', 'Young audience']
  },
  {
    name: 'Etsy',
    logo: '/images/logos/Etsy_logo.svg.png',
    description: 'Perfect for handmade, vintage, and unique items. Connect with craft-focused buyers.',
    status: 'coming-soon',
    category: 'marketplace',
    features: ['Niche audience', 'Handmade focus', 'Global reach']
  },
  // Suppliers
  {
    name: 'Amazon UK',
    logo: '/images/logos/Amazon_logo.svg.png',
    description: 'Source products from 8 Amazon marketplaces (UK, US, DE, FR, IT, ES, AU, CA) with real-time price and stock monitoring.',
    status: 'active',
    category: 'supplier',
    features: ['Price tracking', 'Stock sync', 'Prime detection']
  },
  {
    name: 'Amazon US',
    logo: '/images/logos/Amazon_logo.svg.png',
    description: 'Access millions of products across 8 Amazon marketplaces with smart scheduling and proxy rotation.',
    status: 'active',
    category: 'supplier',
    features: ['Wide selection', 'Fast shipping', 'Price monitoring']
  },
  {
    name: 'Amazon DE',
    logo: '/images/logos/Amazon_logo.svg.png',
    description: 'Source from 8 Amazon marketplaces for European and global dropshipping with VAT handling.',
    status: 'active',
    category: 'supplier',
    features: ['EU products', 'VAT handling', 'Local shipping']
  },
  {
    name: 'AliExpress',
    logo: '/images/logos/Aliexpress_logo.svg.png',
    description: 'Connect to AliExpress for low-cost sourcing with worldwide shipping.',
    status: 'coming-soon',
    category: 'supplier',
    features: ['Low prices', 'Global shipping', 'Huge catalog']
  },
];

const Integrations: React.FC = () => {
  const marketplaces = integrations.filter(i => i.category === 'marketplace');
  const suppliers = integrations.filter(i => i.category === 'supplier');

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const IntegrationCard: React.FC<{ integration: Integration; index: number }> = ({ integration, index }) => (
    <motion.div
      className="integration-card"
      variants={itemVariants}
      whileHover={{ y: -5, boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}
      style={{
        background: 'white',
        borderRadius: '20px',
        padding: '24px',
        position: 'relative',
        overflow: 'hidden',
        border: integration.status === 'active' ? '2px solid #22c55e' : '1px solid #e5e7eb',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Status Badge */}
      <div
        style={{
          position: 'absolute',
          top: '16px',
          right: '16px',
          padding: '4px 12px',
          borderRadius: '20px',
          fontSize: '11px',
          fontWeight: '700',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          background: integration.status === 'active' ? '#dcfce7' : '#fef3c7',
          color: integration.status === 'active' ? '#16a34a' : '#d97706',
        }}
      >
        {integration.status === 'active' ? '✓ Active' : 'Coming Soon'}
      </div>

      {/* Logo */}
      <div
        style={{
          width: '80px',
          height: '50px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          marginBottom: '16px',
          filter: integration.status === 'coming-soon' ? 'grayscale(50%)' : 'none',
          opacity: integration.status === 'coming-soon' ? 0.7 : 1,
        }}
      >
        <img
          src={integration.logo}
          alt={integration.name}
          loading="lazy"
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
          }}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      </div>

      {/* Name */}
      <h4 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '8px', color: '#1f2937' }}>
        {integration.name}
      </h4>

      {/* Description */}
      <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px', lineHeight: '1.6', flex: 1 }}>
        {integration.description}
      </p>

      {/* Features */}
      {integration.features && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {integration.features.map((feature, idx) => (
            <span
              key={idx}
              style={{
                padding: '4px 10px',
                background: '#f3f4f6',
                borderRadius: '6px',
                fontSize: '11px',
                color: '#4b5563',
                fontWeight: '500',
              }}
            >
              {feature}
            </span>
          ))}
        </div>
      )}
    </motion.div>
  );

  return (
    <section id="integrations" style={{ padding: '100px 0', background: 'linear-gradient(180deg, #f9fafb 0%, #ffffff 100%)' }}>
      <Container>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ textAlign: 'center', marginBottom: '60px' }}
        >
          <span
            style={{
              display: 'inline-block',
              padding: '8px 16px',
              background: 'linear-gradient(135deg, #dbeafe, #e0e7ff)',
              borderRadius: '20px',
              fontSize: '13px',
              fontWeight: '600',
              color: '#4f46e5',
              marginBottom: '16px',
            }}
          >
            🔗 Integrations
          </span>
          <h2 style={{ fontSize: '42px', fontWeight: '800', marginBottom: '16px', color: '#1f2937' }}>
            Connect Your Entire
            <span style={{
              background: 'linear-gradient(135deg, #f97316, #fb923c)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginLeft: '10px',
            }}>
              Ecosystem
            </span>
          </h2>
          <p style={{ fontSize: '18px', color: '#6b7280', maxWidth: '600px', margin: '0 auto' }}>
            Seamlessly connect with major marketplaces and suppliers. Manage listings, pricing, orders, and tracking from one powerful dashboard.
          </p>
        </motion.div>

        {/* Selling Channels */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          style={{ marginBottom: '60px' }}
        >
          <h3 style={{
            fontSize: '24px',
            fontWeight: '700',
            marginBottom: '24px',
            color: '#1f2937',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '28px' }}>🏪</span>
            Selling Channels
          </h3>
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <Row className="g-4">
              {marketplaces.map((integration, index) => (
                <Col key={index} lg={4} md={6}>
                  <IntegrationCard integration={integration} index={index} />
                </Col>
              ))}
            </Row>
          </motion.div>
        </motion.div>

        {/* Supplier Sources */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <h3 style={{
            fontSize: '24px',
            fontWeight: '700',
            marginBottom: '24px',
            color: '#1f2937',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '28px' }}>📦</span>
            Supplier Sources
          </h3>
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <Row className="g-4">
              {suppliers.map((integration, index) => (
                <Col key={index} lg={3} md={6}>
                  <IntegrationCard integration={integration} index={index} />
                </Col>
              ))}
            </Row>
          </motion.div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{
            textAlign: 'center',
            marginTop: '60px',
            padding: '40px',
            background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
            borderRadius: '24px',
            color: 'white',
          }}
        >
          <h3 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '12px' }}>
            Need a Different Integration?
          </h3>
          <p style={{ fontSize: '16px', opacity: 0.8, marginBottom: '24px' }}>
            We're constantly adding new integrations. Let us know what you need!
          </p>
          <a
            href="mailto:support@coderom.com"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '14px 28px',
              background: 'linear-gradient(135deg, #f97316, #fb923c)',
              color: 'white',
              borderRadius: '12px',
              fontWeight: '600',
              textDecoration: 'none',
              transition: 'transform 0.2s',
            }}
          >
            Request Integration
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </a>
        </motion.div>
      </Container>
    </section>
  );
};

export default Integrations;
